import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import { isAddressEmpty } from '@libs/NwUtils';

//import { getSimpleContactByID, useContact } from '@hooks/Contact/UseContact';
import { forceReloadContact, useContact } from '@hooks/API/Contact/UseContact';

const getContactConnectionsByID = (ID) => Axios.get(`/contacts/connections/${ID}`);

const fetchRoomConnections = () => Axios.get('/connections/GetRooms');

const forceReloadContactConnections = (ID) => {
    const intID = Number(ID);
    queryClient.invalidateQueries({ queryKey: ['contactconnections', intID] });
};

const updateContactConnections = async (ID) => {
    forceReloadContactConnections(ID);
    forceReloadContact(ID);
};

const useContactConnections = (ID) => {
    const disableQuery = !ID || isNaN(parseInt(ID));
    const intID = !disableQuery ? parseInt(ID) : null;
    return useQuery({
        queryKey: ['contactconnections', intID],
        queryFn: () => getContactConnectionsByID(intID),
        staleTime: 60000,
        cacheTime: 60000,
        enabled: !disableQuery,
    });
};

//DEFAULT CONNECTION

const editContactConnectionItemDefault = ({ itemType, idItem, contactID }) =>
    Axios.get(`/connections/setdefault${itemType}/${idItem}/${contactID}`);

const useEditContactConnectionItemDefault = () => {
    return useMutation({
        mutationFn: editContactConnectionItemDefault,
        onSuccess: (data, variables) => {
            updateContactConnections(variables.contactID);
        },
    });
};

const useIsContactConnectionItemDefault = (itemType, idItem, contactID) => {
    const { data: currentContact } = useContact(contactID);

    if (currentContact && currentContact.DefaultConnection) {
        const { EmailID, AddressID, FaxID, MobileID, PhoneID } = currentContact.DefaultConnection;
        if (currentContact.DefaultConnection) {
            switch (itemType) {
                case 'email':
                    return EmailID === idItem;
                case 'address':
                    return AddressID === idItem;
                case 'phone':
                    return FaxID === idItem || MobileID === idItem || PhoneID === idItem;
                default:
                    return false;
            }
        }
    }
    return false;
};

const isContactConnectionItemDefault = (itemType, idItem, contactID) => {
    const mycontact = queryClient.getQueryData(['contact', contactID]);
    const { EmailID, AddressID, FaxID, MobileID, PhoneID } = mycontact.DefaultConnection;
    if (mycontact.DefaultConnection) {
        switch (itemType) {
            case 'email':
                return EmailID === idItem;
            case 'address':
                return AddressID === idItem;
            case 'phone':
                return FaxID === idItem || MobileID === idItem || PhoneID === idItem;
            default:
                return false;
        }
    }
    return false;
};

// SORT CONNECTIONS
const sortContactConnections = ({ newitemsid, contactID }) =>
    Axios.put(`/connections/SetOrder/${contactID}`, newitemsid);

const useSortContactConnections = () => {
    return useMutation({
        mutationFn: sortContactConnections,
        onSuccess: (data, variables) => {
            updateContactConnections(variables.contactID);
        },
    });
};

// CONNECTIONS (ADD, EDIT, DELETE)
const editContactConnection = (data) => Axios.put(`/connections`, data);

const useEditContactConnection = () => {
    return useMutation({
        mutationFn: editContactConnection,
        onSuccess: (data, variables) => {
            updateContactConnections(data.ContactID);
        },
    });
};

const deleteContactConnection = ({ connectionID, contactID }) => Axios.delete(`/connections/${connectionID}`);

const useDeleteContactConnection = () => {
    return useMutation({
        mutationFn: deleteContactConnection,
        onSuccess: (data, variables) => {
            updateContactConnections(variables.contactID);
        },
    });
};

//GENERIC ITEM (PHONE, EMAIL, ADDRESS)
const editContactConnectionItem = ({ itemType, data, contactID }) => Axios.put(`/connections/${itemType}`, data);

const useEditContactConnectionItem = () => {
    return useMutation({
        mutationFn: editContactConnectionItem,
        onSuccess: (data, variables) => {
            updateContactConnections(variables.contactID);
        },
    });
};

const addContactConnectionItem = ({ itemType, data, contactID }) => Axios.post(`/connections/${itemType}`, data);

const useAddContactConnectionItem = () => {
    return useMutation({
        mutationFn: addContactConnectionItem,
        onSuccess: (data, variables) => {
            updateContactConnections(variables.contactID);
        },
    });
};

const deleteContactConnectionItem = ({ itemType, itemID, contactID }) => {
    let endpoint = '';
    switch (itemType) {
        // case "connection":
        //   endpoint = `connections/${itemID}`
        //   break;
        case 'address':
            endpoint = `/connections/addresses/${itemID}`;
            break;
        case 'email':
            endpoint = `/connections/emails/${itemID}`;
            break;
        case 'phone':
            endpoint = `/connections/phones/${itemID}`;
            break;
        default:
            break;
    }
    return Axios.delete(endpoint);
};

const useDeleteContactConnectionItem = () => {
    return useMutation({
        mutationFn: deleteContactConnectionItem,
        onSuccess: (data, variables) => {
            updateContactConnections(variables.contactID);
        },
    });
};

const addContactConnectionFull = async ({ data }) => {
    const response = await Axios.post(`/connections/AddFull`, data);
    return {
        ...response,
    };
};

const useAddContactConnectionFull = () => {
    return useMutation({
        mutationFn: addContactConnectionFull,
        onSuccess: (data, variables) => {
            updateContactConnections(data.ContactID);
        },
    });
};

// Salva sull'anagrafica del contatto l'indirizzo scelto in un evento: crea una nuova
// connection "Place" oppure aggiorna l'indirizzo della connection selezionata.
// Ritorna il messaggio d'errore da mostrare, o null se è andato tutto bene.
const useSaveAddressOnContact = () => {
    const { mutateAsync: addConnectionFull } = useAddContactConnectionFull();
    const { mutateAsync: editConnectionItem } = useEditContactConnectionItem();

    return async ({ addLocationToCustomer, address }) => {
        if (!addLocationToCustomer || !address || isAddressEmpty(address)) {
            return null;
        }
        const { customerID, connectionID, newConnectionName } = addLocationToCustomer;
        if (!customerID) {
            return 'Select a contact before saving the address on it.';
        }
        if (!connectionID) {
            try {
                await addConnectionFull({
                    data: {
                        ContactID: customerID,
                        Type: 'Place',
                        ConnectionSubTypeID: 4,
                        Name: newConnectionName,
                        Address: { ...address, ID: undefined },
                    },
                });
            } catch {
                return 'An error occurred while creating the new connection. Please try again.';
            }
            return null;
        }
        // senza ID l'indirizzo non è ancora in anagrafica: non c'è una riga da aggiornare
        if (!address.ID || address.ID === 'add-new') {
            return null;
        }
        try {
            await editConnectionItem({
                itemType: 'addresses',
                data: { ConnectionID: connectionID, ...address },
                contactID: customerID,
            });
        } catch {
            return 'An error occurred while updating the address. Please try again.';
        }
        return null;
    };
};

const getRoomConnections = () => {
    return queryClient.getQueryData(['roomConnections']) ? queryClient.getQueryData(['roomConnections']) : [];
};

const prefetchRoomConnections = () => {
    queryClient.prefetchQuery(['roomConnections'], () => fetchRoomConnections(), {
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const reloadRoomConnections = () => {
    queryClient.invalidateQueries({ queryKey: ['roomConnections'] });
};

const getRoomByID = (ID) => Axios.get(`/rooms/${ID}`);

const useRoom = (ID) => {
    const disableQuery = !ID || isNaN(parseInt(ID));
    const intID = !disableQuery ? parseInt(ID) : null;
    return useQuery({
        queryKey: ['room', intID],
        queryFn: () => getRoomByID(intID),
        enabled: !disableQuery,
        staleTime: 60000,
        cacheTime: 60000,
        retry: false,
    });
};

const deleteRoom = ({ roomId }) => Axios.delete(`/rooms/${roomId}`);

const useDeleteRoom = () => {
    return useMutation({
        mutationFn: deleteRoom,
        onSuccess: (data, variables) => {
            if (variables.contactId) {
                forceReloadContactConnections(variables.contactId);
            }
        },
    });
};

const addRoom = ({ data }) => Axios.post('/rooms', data);

const useAddRoom = () => {
    return useMutation({
        mutationFn: addRoom,
        onSuccess: (data, variables) => {
            if (variables.contactId) {
                forceReloadContactConnections(variables.contactId);
            }
        },
    });
};

const updateRoom = ({ data }) => Axios.put('/rooms', data);

const useUpdateRoom = () => {
    return useMutation({
        mutationFn: updateRoom,
        onSuccess: (data, variables) => {
            if (variables.contactId) {
                forceReloadContactConnections(variables.contactId);
            }
        },
    });
};

export {
    useContactConnections,
    updateContactConnections,
    useEditContactConnectionItemDefault,
    isContactConnectionItemDefault,
    useDeleteContactConnection,
    useEditContactConnectionItem,
    useAddContactConnectionItem,
    useSortContactConnections,
    useEditContactConnection,
    //useAddContactConnection,
    useDeleteContactConnectionItem,
    useAddContactConnectionFull,
    useSaveAddressOnContact,
    forceReloadContactConnections,
    getRoomConnections,
    prefetchRoomConnections,
    reloadRoomConnections,
    useDeleteRoom,
    useAddRoom,
    useUpdateRoom,
    useRoom,
    useIsContactConnectionItemDefault,
};
